import { AUTH_ACTION_TYPES } from '../reducers/authReducer';
import { clearServerLocal } from '../../services/localConnections';
import i18n from '../../i18n';
import { clearUserData, migrateGlobalDataToUser } from '../../utils/userStorage';
import { clearChallengeData } from './challengeActions';
import * as qbase from '../../services/qbaseClient';
import { refreshMaintenance } from '../../services/qbaseMaintenance';
import { reloadPage } from '../../utils/reloadPage';

/**
 * 인증 — QBase 테넌트 세션 (LiveSQL 테넌트 편입 1단계, 2026-08-19)
 *
 * before: 브라우저 → Node(:3003) /api/emailverif/login → coreServerClient 가
 *         서비스계정(admin1) 자격으로 코어서버에 폼 로그인 → 쿠키자에 보관.
 *         브라우저가 받은 것은 서버가 검증하지 않는 가짜 토큰(`core_<ts>_<rand>`) 이었고,
 *         SQL 실행 엔드포인트는 애초에 인증을 요구하지 않았다.
 * after : 브라우저가 **자기 자신으로** QBase 에 로그인한다. 인증 표식은 JSESSIONID 쿠키이고
 *         진위 판정은 서버가 한다. 토큰은 만들지 않는다 — 검증하지 않는 토큰은 없느니만 못하다.
 *
 * 로그인 ID 는 QBase 의 lgn_id 다. 이관된 계정은 이메일형(자가 가입 22건)과
 * 교육용 계정형(edu01 … 12건)이 섞여 있으므로 **이메일 형식을 강요하지 않는다.**
 */

/** Redux · localStorage 에 실을 사용자 모양 (기존 소비자들이 읽던 키를 유지) */
function toUserData(sessionUser) {
  if (!sessionUser) return null;
  return {
    usrid: sessionUser.usrid,
    id: sessionUser.usrid,
    username: sessionUser.loginId,
    loginId: sessionUser.loginId,
    name: sessionUser.name || sessionUser.userKnme || sessionUser.loginId,
    email: sessionUser.email || sessionUser.emlAddr || '',
    roleCd: sessionUser.roleCd || '',
    viewOnly: qbase.isViewOnly(sessionUser.viewOnly),
  };
}

function persistUser(userData) {
  // userStorage.getCurrentUserId() 가 이 키를 읽어 사용자별 localStorage 네임스페이스를 만든다
  localStorage.setItem('livesql_user', JSON.stringify(userData));
}

function clearAuthStorage() {
  localStorage.removeItem('livesql_user');
  localStorage.removeItem('livesql_auth_token');   // 구 가짜 토큰 — 남아 있으면 지운다
  localStorage.removeItem('livesql_temp_password');
}

/**
 * 인증이 끝난 **뒤**의 공통 절차 — 작업 시스템 확정 · 상태 반영 · 저장 · 데이터 이관.
 *
 * 아이디/비밀번호와 소셜(Google·Naver)은 **자격증명을 확인하는 방식만** 다르고 그 뒤는
 * 같아야 한다. 경로마다 따로 쓰면 한쪽에서 `ensureActiveSystem` 이 빠지는 날이 오고,
 * 그러면 로그인은 되는데 조회가 전부 0건이 된다(원인이 화면에 안 보인다).
 */
async function finishLogin(dispatch, { user, viewOnly }, getState) {
  if (!user) throw new Error(i18n.t('auth:err.noUserInResponse'));

  // **만료 뒤 다른 계정으로 들어왔다**(SessionExpiredGate 는 앞 사용자의 작업 화면 위에 떠 있다).
  // 상태만 바꾸면 앞 사용자의 열린 파일·결과·연결 목록이 새 사용자에게 보이고, redux-persist 가 그것을
  // 새 사용자의 저장 공간에 옮겨 쓴다(logout 이 연결 목록을 비우는 것과 같은 이유). 서버 세션은 이미 새
  // 사용자이므로 **새로 불러오면** initializeAuth 가 새 사용자로 복원한다. 앞 사용자를 가리키는
  // livesql_user 는 먼저 지운다 — 남으면 복원 전에 앞 사용자의 저장 공간을 읽는다.
  // 앞 사용자의 저장물 자체는 지우지 않는다(그 사람이 다시 온다).
  const auth = getState ? getState().auth : null;
  if (auth && auth.expiredUsrid && String(auth.expiredUsrid) !== String(user.usrid)) {
    clearAuthStorage();
    reloadPage();
    return { switched: true };
  }

  // 작업 시스템 확정 — 이걸 빠뜨리면 읽기 스코프의 축이 안 정해져 조회가 0건이 된다.
  const session = await qbase.getSession();
  await qbase.ensureActiveSystem(session?.homeSysId);

  // 역할 코드 — 로그인 응답은 user.roleCd, 세션 응답은 바깥 activeRoleCd 에 싣는다(QBase 확인 2026-10-05)
  const userData = { ...toUserData({ roleCd: session?.activeRoleCd, ...user, ...session?.user }), viewOnly };

  dispatch({
    type: AUTH_ACTION_TYPES.LOGIN_SUCCESS,
    payload: { user: userData, isTemporaryPassword: false },
  });

  persistUser(userData);

  const migratedCount = migrateGlobalDataToUser(userData.usrid);
  console.log(`Migrated ${migratedCount} global data entries to user-specific storage`);

  if (viewOnly) {
    console.warn('[auth] 조회 전용 세션입니다 — 같은 계정의 다른 세션이 편집권을 쥐고 있습니다.');
  }
}

export const login = (loginId, password) => {
  return async (dispatch, getState) => {
    dispatch({ type: AUTH_ACTION_TYPES.LOGIN_START });
    try {
      await finishLogin(dispatch, await qbase.login(loginId, password), getState);
    } catch (error) {
      dispatch({
        type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
        payload: error.message || i18n.t('auth:err.loginFailed'),
      });
    }
  };
};

/** 체험 계정 로그인 — 서버가 대신 로그인하고, 뒤처리는 다른 길과 같은 finishLogin 이다 */
export const loginTrial = (loginId) => {
  return async (dispatch, getState) => {
    dispatch({ type: AUTH_ACTION_TYPES.LOGIN_START });
    try {
      await finishLogin(dispatch, await qbase.trialLogin(loginId), getState);
    } catch (error) {
      dispatch({
        type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
        payload: error.message || i18n.t('auth:err.loginFailed'),
      });
    }
  };
};

/**
 * 소셜 로그인 (Google · Naver).
 *
 * 실패를 **화면으로 되돌려 준다** — 탈회 계정이면 QBase 가 실패가 아니라 «복구할까,
 * 지울까» 를 묻는 본문(`withdrawn:true`)을 보내는데, 여기서 문자열로 뭉개면 화면이
 * 사용자를 막다른 길에 세운다. 그래서 성공/실패를 돌려주고 본문도 함께 얹는다.
 */
export const loginWithOAuth = (provider, payload) => {
  return async (dispatch, getState) => {
    dispatch({ type: AUTH_ACTION_TYPES.LOGIN_START });
    try {
      const r = provider === 'naver'
        ? await qbase.naverLogin(payload.code, payload.state)
        : await qbase.googleLogin(payload.accessToken);

      // **처음 온 사람은 가입 마무리 단계를 거친다**(사용자 결정 2026-09-12).
      // 여기서 LOGIN_SUCCESS 를 내면 App 이 즉시 본 화면으로 바뀌어, 로그인 화면에 얹은
      // 마무리 단계가 뜰 자리가 없어진다. 그래서 **상태 전환을 미루고** 화면에 넘긴다.
      // (계정과 QBase 세션은 이미 만들어져 있다 — 여기서 그만둬도 다음 방문에 그대로
      //  복원된다. 미루는 것은 앱 상태이지 가입이 아니다)
      if (r.created) {
        dispatch({ type: AUTH_ACTION_TYPES.CLEAR_ERROR });
        return { ok: true, created: true, pending: r };
      }

      await finishLogin(dispatch, r, getState);
      return { ok: true };
    } catch (error) {
      dispatch({
        type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
        payload: error.message || i18n.t('auth:err.loginFailed'),
      });
      return { ok: false, error, body: error.body || null };
    }
  };
};

/**
 * 가입 마무리 — 닉네임을 확정하고 **그때서야** 로그인 상태로 넘어간다.
 *
 * 닉네임은 테넌트 계정(`vw_qbase_user.user_knme`)을 직접 고친다. 화면의 「닉네임 변경」이
 * 쓰는 `/api/emailverif/change-nickname` 은 **공통 저장소**를 바꾸는 경로인데, 소셜로 만든
 * 계정은 테넌트에만 있어 그쪽에서는 찾지 못한다.
 *
 * 닉네임 저장이 실패해도 **로그인은 시킨다.** 이름 하나 때문에 가입한 사람을 로그인 화면에
 * 붙들어 두면, 계정은 이미 있는데 들어갈 수가 없다 — 고칠 길은 앱 안에 있다.
 */
export const completeOAuthSignup = ({ nickname, pending }) => {
  return async (dispatch, getState) => {
    let noticed = null;
    const want = String(nickname || '').trim();
    if (want && want !== (pending.user.name || '')) {
      try {
        await qbase.saveUser({ userKnme: want }, 'update');
        pending = { ...pending, user: { ...pending.user, name: want, userKnme: want } };
      } catch (e) {
        noticed = e.message || i18n.t('auth:err.loginFailed');
      }
    }
    await finishLogin(dispatch, pending, getState);
    return { ok: true, noticed };
  };
};

export const logout = () => {
  return async (dispatch) => {
    console.log('🚪 [Logout] 로그아웃 프로세스 시작');

    // 편집권을 먼저 반납한다 — 안 하면 다음 로그인이 자기 자신 때문에 조회 전용이 된다
    await qbase.releaseEdit();
    // 로컬 전용 연결 — 서버 메모리에 올려 둔 것을 즉시 지운다(로그인 세션이 있을 때 불러야 한다 — 로그아웃 뒤엔 401).
    // 브라우저의 암호문은 남는다(다음 로그인에 다시 올린다)
    await clearServerLocal();
    await qbase.logout();

    dispatch(clearChallengeData());
    /*
      **연결 목록도 비운다.** 남겨 두면 두 가지가 샌다 —
        · 로그인 화면 뒤/다음 로그인 직후에 앞 사용자의 연결이 잠깐 보인다
        · redux-persist 가 그 사이 그것을 **다음 사용자의 저장 공간**에 쓴다
          (userPersistStorage 는 저장 시점의 livesql_user 를 본다)
      한 브라우저에서 계정을 바꿔 가며 쓰면 실제로 남의 연결이 되살아났다(신고 2026-09-12).
    */
    dispatch({ type: 'SET_CONNECTIONS', payload: [] });
    localStorage.removeItem('selectedParticipationGroup');

    const clearedCount = clearUserData();
    console.log(`🚪 [Logout] ${clearedCount}개 사용자별 데이터 삭제`);

    clearAuthStorage();
    dispatch({ type: AUTH_ACTION_TYPES.LOGOUT });

    console.log('🚪 [Logout] 로그아웃 프로세스 완료');
  };
};

/**
 * 앱 시작 시 세션 복원.
 *
 * 예전에는 localStorage 의 사용자 정보와 토큰이 있으면 로그인 상태로 쳤다 —
 * 서버에 물어보지 않았으므로 **서버 세션이 끊긴 뒤에도 로그인처럼 보였고**,
 * 실제 호출에서야 실패했다. 이제는 서버에 묻는다. 그게 유일한 진실이다.
 */
export const initializeAuth = () => {
  return async (dispatch) => {
    dispatch({ type: AUTH_ACTION_TYPES.LOGIN_START });

    // QBase 점검·DB 전환 재기동 중에는 세션을 확인할 수 없다 — 「세션 없음」 으로 단정해 로그아웃시키지 않고
    // 로그인 확인 중 상태로 둔 채 점검 확인 주기(retryAfterSec)마다 다시 묻는다. 배너가 이유를 말한다.
    const retryIfMaintenance = async () => {
      const m = await refreshMaintenance();
      if (!m || !m.active) return false;
      const sec = m.restarting ? 10 : (m.retryAfterSec || 30);
      setTimeout(() => dispatch(initializeAuth()), sec * 1000);
      return true;
    };

    try {
      const session = await qbase.getSession();

      if (!session || !session.user) {
        if (await retryIfMaintenance()) return;
        clearAuthStorage();
        dispatch({ type: AUTH_ACTION_TYPES.LOGOUT });
        return;
      }

      await qbase.ensureActiveSystem(session.homeSysId);

      // 새로고침으로 세션이 되살아났는데 편집권만 놓친 경우가 흔하다(같은 디바이스 좀비 세션).
      // 한 번 승계 시도한다 — 실패해도 조회는 되므로 로그인 자체를 막지 않는다.
      let viewOnly = qbase.isViewOnly(session.user.viewOnly);
      if (viewOnly) viewOnly = !(await qbase.acquireEdit());

      // 새로고침 복원 — 세션 응답의 user 에는 roleCd 가 없고 activeRoleCd 가 바깥에 있다(챌린지 수정 단추가 이것을 본다)
      const userData = { ...toUserData({ roleCd: session.activeRoleCd, ...session.user }), viewOnly };
      persistUser(userData);

      dispatch({
        type: AUTH_ACTION_TYPES.LOGIN_SUCCESS,
        payload: { user: userData, isTemporaryPassword: false },
      });
    } catch (error) {
      console.error('[auth] 세션 복원 실패:', error.message);
      if (await retryIfMaintenance().catch(() => false)) return;
      clearAuthStorage();
      dispatch({ type: AUTH_ACTION_TYPES.LOGOUT });
    }
  };
};

/**
 * **로그인이 만료됐는지 확인하고, 만료됐으면 작업 화면 위에 로그인 창을 띄운다** (2026-09-14).
 *
 * 앱을 연 채로 QBase 세션이 끊기면(유휴 만료·QBase 재기동) 화면은 로그인 상태로 남고 모든 호출이 401 로
 * 실패했다 — 사용자에게는 "갑자기 아무것도 안 된다" 로 보였고, 세션 폴링은 3초마다 영원히 401 을 냈다
 * (사흘 동안 68,270건, docs/features/16-ops-log.md). 로그인 화면은 새로고침해야만 떴다.
 *
 *   · **서버의 401 한 번으로 단정하지 않는다.** QBase 에 직접 물어 `none` 일 때만 만료로 친다. 백엔드
 *     설정(QBASE_INTERNAL_URL)이나 쿠키 경로 문제로 난 401 에 로그인 창을 띄우면 다시 로그인해도 풀리지
 *     않는다. 확인할 수 없으면(`unknown`) 그대로 둔다.
 *   · **로그아웃이 아니다.** `logout()` 은 사용자별 저장물을 지운다 — 만료는 사용자가 떠난 것이 아니므로
 *     작업을 그대로 두고 로그인 상태만 내린다.
 *   · 여러 곳에서 동시에 불려도 확인은 한 번(세션 폴링과 SQL 실행이 같은 순간 401 을 받는다).
 */
let expiryCheck = null;
export const handleSessionExpired = () => (dispatch, getState) => {
  const auth = (getState && getState().auth) || {};
  if (!auth.isAuthenticated) return Promise.resolve(false);
  if (expiryCheck) return expiryCheck;
  expiryCheck = (async () => {
    try {
      if (await qbase.checkSession() !== 'none') return false;
      const u = auth.user || {};
      dispatch({ type: AUTH_ACTION_TYPES.SESSION_EXPIRED, payload: { usrid: u.usrid || u.id || null } });
      return true;
    } catch (e) {
      return false;
    } finally {
      expiryCheck = null;
    }
  })();
  return expiryCheck;
};

export const clearAuthError = () => {
  return { type: AUTH_ACTION_TYPES.CLEAR_ERROR };
};

export const passwordChanged = () => {
  return (dispatch) => {
    localStorage.setItem('livesql_temp_password', 'false');
    dispatch({ type: AUTH_ACTION_TYPES.PASSWORD_CHANGED });
  };
};
